import { FC, ReactNode, useMemo } from 'react';

import classNames from 'classnames';

import Container, { ContainerProps } from '../Container';

import './Card.scss';

import { TestMetadata } from '../types';

export interface CardProps {
  /** Elements to Wrap */
  children: ReactNode;
  /** Class name */
  className?: string;
  /** Determines if the whole card is an interactive object*/
  isInteractive?: boolean;
  /** Determines if the card in a active state */
  isActive?: boolean;
  /** Event to fire when the card is clicked */
  onClick?: () => void;
  /** Determines what kind of background style has the card - backed (default), floating */
  cardStyle?: 'backed' | 'floating';
  /** Determines what kind of orientation will have the children of the card - stacked (default), inline */
  orientation?: 'stacked' | 'inline';
  // Test IDs for sections of AccordionPanel
  testMetadata?: TestMetadata;
  /** Set the container props the card uses */
  containerProps?: Omit<ContainerProps, 'children'>;
}

const Card: FC<CardProps> = ({
  children,
  isActive,
  isInteractive,
  className,
  cardStyle = 'backed',
  orientation = 'stacked',
  containerProps,
  testMetadata,
  ...otherProps
}) => {
  const cardClassNames = useMemo(
    () =>
      classNames(
        'sb-card',
        {
          'sb-card_default-padding': !containerProps?.padding,
          'sb-card_active': isActive,
          'sb-card_interactive': isInteractive,
          'sb-card_inline': orientation === 'inline',
          'sb-card_stacked': orientation === 'stacked',
        },
        className,
      ),
    [className, isActive, isInteractive, orientation, containerProps?.padding],
  );

  const cardContainerProps = useMemo(() => {
    let cardProps: Partial<ContainerProps> = {};

    if (cardStyle === 'backed') {
      cardProps = {
        ...cardProps,
        fill: 'white',
        strokeWidth: 'thin',
        strokeColor: 'white', // in the css this was transparent
        cornerRadius: 'large',
        elevation: 'low',
      };
    }

    if (isActive) {
      cardProps = {
        ...cardProps,
        fill: 'theme',
        strokeWidth: 'thin',
      };
    }

    return { ...cardProps, ...containerProps };
  }, [containerProps, isActive, cardStyle]);

  return (
    <Container
      {...testMetadata}
      {...otherProps}
      {...cardContainerProps}
      className={cardClassNames}
    >
      {children}
    </Container>
  );
};

export default Card;
